const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')

const ACCESS_TOKEN_KEY = 'cadence_access_token'
const REFRESH_TOKEN_KEY = 'cadence_refresh_token'

let refreshRequest = null

function normalizeRole(role) {
  if (!role || role === 'admin' || role === 'org_admin') {
    return role
  }

  return role.endsWith('admin') ? 'admin' : role
}

export function getApiBaseUrl() {
  if (API_URL) {
    return API_URL
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '')
  }

  throw new Error('REACT_APP_API_URL is not configured.')
}

function getApiUrl(path) {
  return `${getApiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

function storeTokens({ accessToken, refreshToken }) {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

function dispatchLogout() {
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

function decodeBase64Url(value) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4 || 4)) % 4)
  const decoded = atob(padded)

  return decodeURIComponent(Array.from(decoded)
    .map(character => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
    .join(''))
}

function decodeToken(accessToken) {
  if (!accessToken) {
    return null
  }

  try {
    const [, payload] = accessToken.split('.')
    if (!payload) {
      return null
    }

    const parsed = JSON.parse(decodeBase64Url(payload))
    if (!parsed?.exp || parsed.exp * 1000 <= Date.now()) {
      return null
    }

    return {
      id: parsed.sub,
      email: parsed.email,
      organisationId: parsed.org || null,
      role: normalizeRole(parsed.role)
    }
  } catch {
    return null
  }
}

async function parseResponse(response) {
  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  }

  const text = await response.text()
  return text ? { message: text } : null
}

export async function requestRefresh() {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return false
  }

  if (!refreshRequest) {
    refreshRequest = (async () => {
      const response = await fetch(getApiUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      })

      if (!response.ok) {
        clearTokens()
        dispatchLogout()
        return false
      }

      const data = await response.json()
      storeTokens(data)
      return true
    })()

    refreshRequest.finally(() => {
      refreshRequest = null
    })
  }

  return refreshRequest
}

function buildHeaders(headers?: HeadersInit, body?: BodyInit | null, token?: string | null) {
  const nextHeaders = new Headers(headers || {})

  if (!(body instanceof FormData) && !nextHeaders.has('Content-Type')) {
    nextHeaders.set('Content-Type', 'application/json')
  }

  if (token) {
    nextHeaders.set('Authorization', `Bearer ${token}`)
  }

  return nextHeaders
}

export async function apiFetch(path: string, options: RequestInit = {}, shouldRetry = true) {
  const token = getAccessToken()
  const headers = buildHeaders(options.headers, options.body, token)
  const response = await fetch(getApiUrl(path), {
    ...options,
    headers
  })

  if (response.status === 401 && shouldRetry && path !== '/api/auth/refresh') {
    const refreshed = await requestRefresh()
    if (refreshed) {
      return apiFetch(path, options, false)
    }
  }

  if (!response.ok) {
    const errorBody = await parseResponse(response)
    const message = errorBody?.error || errorBody?.message || 'Request failed.'
    throw new Error(message)
  }

  return parseResponse(response)
}

export const auth = {
  async login(email, password) {
    const data = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }, false)

    storeTokens(data)
    return decodeToken(data?.accessToken)
  },

  async loginWithPasskey(email, passkeyCredentialJson) {
    const data = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, passkeyCredentialJson })
    }, false)

    storeTokens(data)
    return decodeToken(data?.accessToken)
  },

  async register(email, password, username, fullName) {
    return apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, username, fullName })
    }, false)
  },

  async logout() {
    const refreshToken = getRefreshToken()

    try {
      if (refreshToken) {
        await apiFetch('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken })
        }, false)
      }
    } finally {
      clearTokens()
      dispatchLogout()
    }
  },

  getUser() {
    const user = decodeToken(getAccessToken())
    if (!user) {
      clearTokens()
    }

    return user
  },

  isAuthenticated() {
    return Boolean(this.getUser())
  }
}

export async function ssoProbe(email: string): Promise<{ type: string; redirectUrl: string | null; label: string | null }> {
  return apiFetch(`/api/auth/sso/probe?email=${encodeURIComponent(email)}`, {}, false)
}

export function mapOrganisationDto(organisation) {
  if (!organisation) {
    return null
  }

  return {
    id: organisation.id,
    name: organisation.name,
    slug: organisation.slug,
    primary_colour: organisation.primaryColour,
    secondary_colour: organisation.secondaryColour,
    logo_url: organisation.logoUrl,
    powered_by_cadence: organisation.poweredByCadence,
    licence_tier: organisation.licenceTier,
    ai_enabled: organisation.aiEnabled,
    button_colour: organisation.buttonColour,
    header_colour: organisation.headerColour,
    accent_colour: organisation.accentColour,
    licensed_user_count: organisation.licensedUserCount,
    licence_active: organisation.licenceActive,
    org_profile: organisation.orgProfile,
    sharepoint_url: organisation.sharepointUrl,
    instance_type: organisation.instanceType,
    created_by: organisation.createdBy,
    created_at: organisation.createdAt,
    custom_css_url: organisation.customCssUrl,
    custom_css_overrides: organisation.customCssOverrides,
    heading_font_url: organisation.headingFontUrl,
    body_font_url: organisation.bodyFontUrl,
    page_bg_colour: organisation.pageBgColour,
    page_text_colour: organisation.pageTextColour,
    pptx_template_url: organisation.pptxTemplateUrl,
    sso_provider: organisation.ssoProvider,
    sso_login_label: organisation.ssoLoginLabel,
    sso_domain_hint: organisation.ssoDomainHint,
    sso_enforce_password_disabled: organisation.ssoEnforcePasswordDisabled,
    saml_entity_id: organisation.samlEntityId,
    saml_metadata_xml: organisation.samlMetadataXml,
    azure_ad_tenant_id: organisation.azureAdTenantId,
    azure_ad_client_id: organisation.azureAdClientId
  }
}

export function mapOpportunityDto(opportunity) {
  if (!opportunity) {
    return null
  }

  return {
    ...opportunity,
    organisation_id: opportunity.organisation_id ?? opportunity.organisationId ?? null,
    buyer_organisation: opportunity.buyer_organisation ?? opportunity.buyerOrganisation ?? null,
    contract_value_min: opportunity.contract_value_min ?? opportunity.contractValueMin ?? null,
    contract_value_max: opportunity.contract_value_max ?? opportunity.contractValueMax ?? null,
    salesforce_url: opportunity.salesforce_url ?? opportunity.salesforceUrl ?? null,
    framework_id: opportunity.framework_id ?? opportunity.frameworkId ?? null,
    tc_url: opportunity.tc_url ?? opportunity.tcUrl ?? null,
    bid_lead_id: opportunity.bid_lead_id ?? opportunity.bidLeadId ?? null,
    bid_lead: opportunity.bid_lead ?? opportunity.bidLead ?? null,
    qualification_recommendation: opportunity.qualification_recommendation ?? opportunity.qualificationRecommendation ?? null,
    outcome_detail: opportunity.outcome_detail ?? opportunity.outcomeDetail ?? null,
    bid_health: opportunity.bid_health ?? opportunity.bidHealth ?? null,
    created_by: opportunity.created_by ?? opportunity.createdBy ?? null,
    created_at: opportunity.created_at ?? opportunity.createdAt ?? null,
    updated_at: opportunity.updated_at ?? opportunity.updatedAt ?? null
  }
}

export function mapProfileDto(user, organisation = null) {
  if (!user) {
    return null
  }

  return {
    id: user.id,
    email: user.email,
    username: user.username,
    full_name: user.fullName,
    role: normalizeRole(user.role),
    organisation_id: user.organisationId,
    is_active: user.isActive,
    onboarding_dismissed: user.onboardingDismissed,
    created_at: user.createdAt,
    organisations: mapOrganisationDto(organisation)
  }
}

export function mapOrgUserDto(user) {
  if (!user) {
    return null
  }

  return {
    id: user.id,
    email: user.email,
    username: user.username,
    full_name: user.fullName,
    role: normalizeRole(user.role),
    organisation_id: user.organisationId,
    is_active: user.isActive,
    onboarding_dismissed: user.onboardingDismissed,
    created_at: user.createdAt
  }
}